import React, { useEffect, useState } from 'react';
import { Button, Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Description from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/description.svg';
import Flag from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/flag.svg';
import Notepad from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/notepad.svg';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import styles from '../styles.module.scss';
import { ICase, Status } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { formatDate } from '@brands/Utils/formatDate';
import { getUserRoleName } from '@brands/Utils/getFriendlyName';
import linkifyHtml from 'linkify-html';
import sanitizeHtml from 'sanitize-html';
import { checkCollapsibleHeaderColor } from '@brands/Utils/getCollapsibleHeader';
import { Goal } from '@brands/services/careCoordinationReport/types/types';
import ConfirmActionModal from '../Modals/ConfirmActionModal/ConfirmActionModal';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { deleteOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/deleteOralHealthCoachingReportObject';
import { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';

type Props = {
  openInternalNotesModal: () => void;
  openGoalModal: () => void;
  openConsultationSummaryModal: () => void;
  thisCase: ICase;
  userInfo: UserProfile;
  setEditingGoal: React.Dispatch<React.SetStateAction<Goal | null>>;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  onDeleteConsultationSummary: () => Promise<void>;
  errors?: FieldErrors<OralHealthReportSubmit>;
};

const OralHealthCoachingReportCard = ({
  openInternalNotesModal,
  openGoalModal,
  openConsultationSummaryModal,
  thisCase,
  userInfo,
  setEditingGoal,
  setCase,
  onDeleteConsultationSummary,
  errors,
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeGoalIndex, setActiveGoalIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingGoal, setIsDeletingGoal] = useState(false);

  const canEdit =
    userInfo.id === thisCase.care_advisor_id &&
    thisCase?.status === 'in_progress_care_advisor' &&
    userInfo.role.name === UserRoleName.CareAdvisor;
  const [summaryMenuAnchorEl, setSummaryMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteSummaryModalOpen, setIsDeleteSummaryModalOpen] = useState(false);
  const [isDeletingSummary, setIsDeletingSummary] = useState(false);

  const caseGoals = thisCase.oral_health_coaching_report?.care_plan?.goals || [];

  const sanitizeDisplayHtml = (html: string): string => {
    const linked = linkifyHtml(html || '', {
      defaultProtocol: 'https',
      attributes: { target: '_blank', rel: 'noopener noreferrer' },
    });

    return sanitizeHtml(linked, {
      allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['class'],
        li: ['data-list'],
      },
    });
  };

  const isAssignedCA =
    userInfo.role.name === UserRoleName.CareAdvisor && Number(userInfo.id) === Number(thisCase.care_advisor_id);

  // Internal notes double as chart addendums, so the assigned CA can still add
  // them after the case is completed, unlike the rest of the report.
  const canAddInternalNote =
    isAssignedCA && (thisCase.status === Status.InProgressCareAdvisor || thisCase.status === Status.Completed);

  const hasCaseNote =
    !!thisCase.oral_health_coaching_report?.internal_notes &&
    thisCase.oral_health_coaching_report.internal_notes.length > 0;

  const hasCaseGoal = caseGoals.length > 0;

  const canEditGoals = isAssignedCA && checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') === 'edit';

  const consultationSummary = thisCase.oral_health_coaching_report?.care_plan?.consultation_summary || '';

  const hasConsultationSummary = Boolean(consultationSummary?.trim());

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
    setActiveGoalIndex(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number): void => {
    if (!canEditGoals) return;

    setMenuAnchorEl(event.currentTarget);
    setActiveGoalIndex(index);
  };

  const handleAddGoal = (): void => {
    if (!canEdit) return;

    setEditingGoal(null);
    openGoalModal();
  };

  const handleEditGoal = (): void => {
    if (activeGoalIndex === null || !caseGoals[activeGoalIndex] || !canEditGoals) {
      return;
    }

    setEditingGoal(caseGoals[activeGoalIndex]);
    handleMenuClose();
    openGoalModal();
  };

  const handleDeleteGoalClick = (): void => {
    if (activeGoalIndex === null || !caseGoals[activeGoalIndex] || !canEditGoals) {
      return;
    }

    setIsDeleteModalOpen(true);
    setMenuAnchorEl(null);
  };

  const confirmDeleteGoal = async (): Promise<void> => {
    const reportId = thisCase.oral_health_coaching_report?.id;

    if (!reportId || activeGoalIndex === null || !caseGoals?.[activeGoalIndex]?.id) {
      return;
    }

    try {
      setIsDeletingGoal(true);

      const goalId = Number(caseGoals[activeGoalIndex].id);

      const updatedReport = await deleteOralHealthCoachingReportObject(reportId.toString(), goalId.toString(), 'goal');

      setCase((prev) => ({
        ...prev,
        oral_health_coaching_report: updatedReport,
      }));

      setIsDeleteModalOpen(false);
      setActiveGoalIndex(null);
      setEditingGoal(null);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsDeletingGoal(false);
    }
  };

  const handleSummaryMenuClose = (): void => {
    setSummaryMenuAnchorEl(null);
  };

  const handleSummaryMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    if (!canEdit) return;
    setSummaryMenuAnchorEl(event.currentTarget);
  };

  const handleEditConsultationSummary = (): void => {
    handleSummaryMenuClose();
    openConsultationSummaryModal();
  };

  const handleDeleteConsultationSummaryClick = (): void => {
    handleSummaryMenuClose();
    setIsDeleteSummaryModalOpen(true);
  };

  const confirmDeleteConsultationSummary = async (): Promise<void> => {
    try {
      setIsDeletingSummary(true);
      await onDeleteConsultationSummary();
      setIsDeleteSummaryModalOpen(false);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsDeletingSummary(false);
    }
  };

  return (
    <div className={styles.consultationPageRightSideFrameCaseDetails}>
      <div className={styles.consultationPageRightSideFrameCaseDetailsTitle} onClick={() => setIsOpen((p) => !p)}>
        <div className={styles.consultationPageRightSideFrameCaseDetailsTitleDiv}>
          <span>Oral Health Coaching report</span>

          <IconButton size="small">
            <KeyboardArrowDownIcon
              style={{
                transition: '0.25s',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </div>

        <Divider orientation="horizontal" flexItem className={styles.divider} />
      </div>

      {isOpen && (
        <div className={styles.oralHealthCoachingReportBody}>
          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasCaseNote ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            }`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={() => {
                if (!canAddInternalNote) return;
                openInternalNotesModal();
              }}
              style={{ opacity: canAddInternalNote ? 1 : 0.5, pointerEvents: canAddInternalNote ? 'auto' : 'none' }}
            >
              <Tooltip title={!isAssignedCA ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildInternalNotes}>
                  <img src={Description} />
                </span>
              </Tooltip>

              {!hasCaseNote ? (
                <Button variant="text">Add internal case notes</Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Internal case notes</span>
                  <Button variant="text">
                    <AddIcon /> Add
                  </Button>
                </div>
              )}
            </div>

            {hasCaseNote && (
              <div className={styles.noteBody}>
                {thisCase.oral_health_coaching_report?.internal_notes
                  ?.sort((a, b) => {
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);

                    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                      return 0;
                    }

                    return dateA.getTime() - dateB.getTime();
                  })
                  .map((note, index) => (
                    <React.Fragment key={note.id ?? index}>
                      <Divider orientation="horizontal" flexItem className={styles.divider} />
                      <div className={`fs-exclude ${styles.caseNotesContent}`}>
                        <div className={`fs-exclude ${styles.caseNotesContentHeader}`}>
                          <span>{formatDate(note.created_at) || ''}</span>
                          <Divider orientation="vertical" flexItem className={styles.divider} />
                          <span>
                            {note?.created_by?.full_name} ({getUserRoleName(note?.created_by?.role.name)})
                          </span>
                        </div>

                        <div className={`fs-exclude ${styles.caseNotesContentContent}`}>
                          <span
                            className={styles.caseNotesContentText}
                            dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(note.note) }}
                          />
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>

          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasCaseGoal ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            }`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={handleAddGoal}
              style={{ opacity: canEdit ? 1 : 0.5, pointerEvents: canEdit ? 'auto' : 'none' }}
            >
              <Tooltip title={!isAssignedCA ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildGoal}>
                  <img src={Flag} />
                </span>
              </Tooltip>

              {!hasCaseGoal ? (
                <Button variant="text">Add goal</Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Goals</span>
                  <Button variant="text">
                    <AddIcon /> Add
                  </Button>
                </div>
              )}
            </div>
            {hasCaseGoal && caseGoals && caseGoals.length > 0 && (
              <div className={styles.goalsBody}>
                {caseGoals.map((goal, index) => (
                  <React.Fragment key={goal.id ?? index}>
                    <Divider orientation="horizontal" flexItem className={styles.divider} />
                    <div className={styles.goalCardItem}>
                      <div className={styles.goalCardItemHeader}>
                        <span className={styles.goalCardItemTitle}>Goal {index + 1}</span>

                        {checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') === 'edit' && (
                          <IconButton
                            size="small"
                            className={styles.goalCardItemMenuButton}
                            onClick={(e) => handleMenuOpen(e, index)}
                          >
                            <MoreHorizIcon className={styles.moreIcon} />
                          </IconButton>
                        )}
                      </div>
                      {!!goal.goal_text && (
                        <div
                          className={styles.goalCardItemText}
                          dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(goal.goal_text) }}
                        />
                      )}

                      {goal.educations?.length > 0 && (
                        <div className={styles.goalCardItemEducations}>
                          {goal.educations.map((education) => {
                            const hasUrl = Boolean(education.url);

                            return (
                              <div key={education.id} className={styles.goalCardItemEducationRow}>
                                {hasUrl ? (
                                  <a
                                    href={education.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.goalCardItemEducationLink}
                                  >
                                    {education.title || education.url} <InsertLinkIcon />
                                  </a>
                                ) : (
                                  <span className={styles.goalCardItemEducationText}>{education.title}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {index < caseGoals.length - 1 && (
                      <Divider orientation="horizontal" flexItem className={styles.goalCardDivider} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEditGoal}>Edit</MenuItem>
              <MenuItem onClick={handleDeleteGoalClick} sx={{ color: 'error.main' }}>
                Delete
              </MenuItem>
            </Menu>
          </div>

          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasConsultationSummary ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            } ${errors?.care_plan?.consultation_summary ? styles.followUpSectionError : ''}`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={() => {
                if (!canEdit) return;
                openConsultationSummaryModal();
              }}
              style={{ opacity: canEdit ? 1 : 0.5, pointerEvents: canEdit ? 'auto' : 'none' }}
            >
              <Tooltip title={!isAssignedCA ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildConsultationSummary}>
                  <img src={Notepad} />
                </span>
              </Tooltip>

              {!hasConsultationSummary ? (
                <Button
                  variant="text"
                  className={errors?.care_plan?.consultation_summary ? styles.followUpButtonError : ''}
                >
                  Add consultation summary (required)
                </Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Consultation summary</span>
                </div>
              )}
            </div>

            {hasConsultationSummary && (
              <div className={styles.consultationSummaryBody}>
                <Divider orientation="horizontal" flexItem className={styles.divider} />
                <div className={styles.consultationSummaryBodyContent}>
                  <div
                    className={styles.consultationSummaryContent}
                    dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(consultationSummary) }}
                  />
                  {canEdit && (
                    <IconButton size="small" onClick={(e) => handleSummaryMenuOpen(e)}>
                      <MoreHorizIcon className={styles.moreIcon} />
                    </IconButton>
                  )}
                </div>
              </div>
            )}

            <Menu anchorEl={summaryMenuAnchorEl} open={Boolean(summaryMenuAnchorEl)} onClose={handleSummaryMenuClose}>
              <MenuItem onClick={handleEditConsultationSummary}>Edit</MenuItem>
            </Menu>

            {errors?.care_plan?.consultation_summary?.message && (
              <div className={styles.sectionErrorText}>{errors.care_plan.consultation_summary.message}</div>
            )}
          </div>
        </div>
      )}
      <ConfirmActionModal
        open={isDeleteModalOpen}
        title="Delete education"
        message={`Are you sure you want to delete this goal item?`}
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        isLoading={isDeletingGoal}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteGoal}
      />
    </div>
  );
};

export default OralHealthCoachingReportCard;
